import { useEffect, useState, useRef } from 'react'
import { useParticipants } from '@livekit/components-react'

// Match useProximityState thresholds exactly
const SPEAKING_AUDIO_LEVEL_THRESHOLD = 0.25
const SPEAKING_LAST_SPOKE_MS = 500
const SPEAKING_UPDATE_INTERVAL_MS = 200

// Efficient set comparison without JSON.stringify
function areSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const item of a) {
    if (!b.has(item)) return false
  }
  return true
}

/**
 * Hook to track which participants are currently speaking.
 *
 * Uses the same polling-based detection as useProximityState (the game-world
 * video overlays) so that speech highlights inside the ScreenShareModal match
 * what you see outside.
 *
 * Detection (OR-based, every 200 ms):
 *  - participant.isSpeaking
 *  - participant.audioLevel > 0.25
 *  - participant.lastSpokeAt within 500 ms
 */
export const useSpeakingParticipants = () => {
  const participants = useParticipants()
  const [speakingIds, setSpeakingIds] = useState<Set<string>>(new Set())
  const [speakingOrder, setSpeakingOrder] = useState<string[]>([])

  // Keep lastSpeakingTimes in a ref so the interval callback always reads the
  // latest value without being in the effect's dependency array.
  const lastSpeakingTimesRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    const updateSpeaking = () => {
      const newSpeakingIds = new Set<string>()
      const currentTime = Date.now()
      const lastSpeakingTimes = lastSpeakingTimesRef.current

      participants.forEach((participant) => {
        const isSpeaking = participant.isSpeaking

        // Enhanced speaking detection — identical logic to useProximityState
        const isActuallySpeaking =
          isSpeaking ||
          (participant.audioLevel !== undefined &&
            participant.audioLevel > SPEAKING_AUDIO_LEVEL_THRESHOLD) ||
          (participant.lastSpokeAt !== undefined &&
            currentTime - (participant.lastSpokeAt as unknown as number) <
              SPEAKING_LAST_SPOKE_MS)

        if (isActuallySpeaking) {
          newSpeakingIds.add(participant.identity)
          lastSpeakingTimes.set(participant.identity, currentTime)
        }
      })

      // Only update state when the set actually changed
      setSpeakingIds((prev) => {
        if (areSetsEqual(newSpeakingIds, prev)) return prev
        return newSpeakingIds
      })

      // Update speaking order — move current speakers to the front
      setSpeakingOrder((prevOrder) => {
        const newOrder = [...prevOrder]

        // Move every currently-speaking participant to the front
        newSpeakingIds.forEach((id) => {
          const idx = newOrder.indexOf(id)
          if (idx !== -1) {
            newOrder.splice(idx, 1)
          }
          newOrder.unshift(id)
        })

        // Deduplicate (in case of race)
        const seen = new Set<string>()
        const deduped: string[] = []
        for (const id of newOrder) {
          if (!seen.has(id)) {
            seen.add(id)
            deduped.push(id)
          }
        }

        // Only update if actually changed
        if (
          deduped.length === prevOrder.length &&
          deduped.every((id, i) => id === prevOrder[i])
        ) {
          return prevOrder
        }
        return deduped
      })
    }

    const intervalId = setInterval(updateSpeaking, SPEAKING_UPDATE_INTERVAL_MS)
    updateSpeaking() // Initial call

    return () => clearInterval(intervalId)
  }, [participants])

  return { speakingIds, speakingOrder }
}
